import React, { useState, useEffect, useMemo } from 'react';
import Button from './Button';
import AcademicYearService from '../../services/AcademicYearService';
import ScheduleEntry from '../../models/ScheduleEntry';
import { conflictManager } from '../../services/ConflictManager';
import {
    getWeeksOutsideResolvedPeriod,
    isWeekInWeekRange
} from '../../utils/schedulePeriodResolver';

/**
 * Weeks from start_week through end_week, including cross-academic-year ranges (e.g. 52–4).
 * @returns {number[]}
 */
function buildWeekNumbersFromStartEnd(startWeek, endWeek) {
    const weeks = [];
    if (endWeek >= startWeek) {
        for (let week = startWeek; week <= endWeek; week += 1) {
            weeks.push(week);
        }
    } else {
        for (let week = startWeek; week <= 53; week += 1) {
            weeks.push(week);
        }
        for (let week = 1; week <= endWeek; week += 1) {
            weeks.push(week);
        }
    }
    return weeks;
}

/**
 * @typedef {Object} ScheduleEntryWeekPickerProps
 * @property {string} [value=''] - The week series string value
 * @property {function} onChange - Callback when week selection changes
 * @property {number} [currentYear] - Current academic year
 * @property {string} [className=''] - CSS class name
 * @property {Object|null} [exceptionsData] - Exception data for display
 * @property {Object|null} [conflictDetectionData] - Data for conflict detection
 * @property {boolean} [isConflictDetectionEnabled=false] - Whether conflict detection is enabled
 * @property {function|null} [onCreateException] - Callback when user wants to create an exception for a specific week
 * @property {Object|null} [resolvedPeriod] - Resolved period scope for warning-only validation
 * @property {string} [scheduleDay=''] - Day name (e.g. monday) — when set, week hover tooltip shows that weekday's date instead of Mon–Sun range
 */

/**
 * @param {ScheduleEntryWeekPickerProps} props
 */
const ScheduleEntryWeekPicker = ({
    value = '',
    onChange,
    currentYear = new Date().getFullYear(),
    className = '',
    exceptionsData = null, // { exceptions: [...], originalEntry: {...} }
    // New props for conflict detection
    conflictDetectionData = null, // { formData, baseScheduleItems, teachers, groups, subjects }
    isConflictDetectionEnabled = false,
    // New prop for exception creation
    onCreateException = null, // Callback when user wants to create/edit an exception for a specific week: (weekNumber: number) => void
    resolvedPeriod = null,
    scheduleDay = ''
}) => {
    const [selectedWeeks, setSelectedWeeks] = useState(new Set());
    const [hoveredWeek, setHoveredWeek] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
    const [academicYearStructure, setAcademicYearStructure] = useState(null);
    const [weekConflicts, setWeekConflicts] = useState(new Map()); // Map of weekNumber -> conflict info

    // Context menu state
    const [contextMenu, setContextMenu] = useState(null); // { week, x, y }

    const academicYearService = useMemo(() => AcademicYearService.getInstance(), []);

    // Fetch academic year structure
    useEffect(() => {
        const fetchAcademicYearStructure = async () => {
            try {
                const structure = await academicYearService.getAcademicYearStructure();
                setAcademicYearStructure(structure);
            } catch (error) {
                console.warn('[ScheduleEntryWeekPicker] Failed to fetch academic year structure:', error);
                // Continue without academic year structure - will fall back to current year
            }
        };

        fetchAcademicYearStructure();
    }, [academicYearService]);

    /** Approximate ISO-style week index used elsewhere in this picker (matches “current week” ring). */
    const getCurrentWeek = () => {
        const now = new Date();
        const start = new Date(now.getFullYear(), 0, 1);
        const diff = now - start;
        const oneWeek = 1000 * 60 * 60 * 24 * 7;
        return Math.ceil(diff / oneWeek);
    };

    const effectivePeriodBounds = useMemo(() => {
        if (resolvedPeriod?.startWeek != null && resolvedPeriod?.endWeek != null) {
            return { startWeek: resolvedPeriod.startWeek, endWeek: resolvedPeriod.endWeek };
        }
        if (academicYearStructure?.startWeek != null && academicYearStructure?.endWeek != null) {
            return { startWeek: academicYearStructure.startWeek, endWeek: academicYearStructure.endWeek };
        }
        return null;
    }, [resolvedPeriod, academicYearStructure]);

    const showThisWeekShortcut =
        effectivePeriodBounds != null
        && isWeekInWeekRange(
            getCurrentWeek(),
            effectivePeriodBounds.startWeek,
            effectivePeriodBounds.endWeek
        );

    // Helper function to get the correct calendar year for a week number
    const getCalendarYearForWeek = (weekNumber) => {
        if (
            resolvedPeriod?.startWeekYear != null
            && resolvedPeriod?.startWeek != null
            && resolvedPeriod?.endWeek != null
        ) {
            const helperLabel = `${resolvedPeriod.startWeekYear}-${resolvedPeriod.startWeekYear + 1}`;
            return AcademicYearService.mapWeekToCalendarYear(
                weekNumber,
                helperLabel,
                resolvedPeriod.startWeek,
                resolvedPeriod.endWeek
            );
        }

        if (!academicYearStructure) {
            // Fallback to current year if no academic structure available
            return currentYear;
        }

        return AcademicYearService.mapWeekToCalendarYear(
            weekNumber,
            academicYearStructure.academicYear,
            academicYearStructure.startWeek,
            academicYearStructure.endWeek
        );
    };

    // Check conflicts for individual weeks
    const checkConflictsForWeek = useMemo(() => {
        return (weekNumber) => {
            if (!isConflictDetectionEnabled || !conflictDetectionData) {
                return null;
            }

            const { formData, baseScheduleItems, teachers, groups, subjects } = conflictDetectionData;

            // Skip conflict checking for events
            if (formData.type === 'event') {
                return null;
            }

            // Parse time from form data
            const localEditTimeValue = formData.time || '';
            const timeParts = localEditTimeValue.split(' - ');
            if (timeParts.length !== 2) {
                return null;
            }

            const timeStart = timeParts[0].trim();
            const timeEnd = timeParts[1].trim();

            try {
                // Create a temporary schedule entry for just this week
                const tempScheduleEntryData = {
                    id: formData.id || 'temp-conflict-check',
                    day: formData.day,
                    timeStart: timeStart,
                    timeEnd: timeEnd,
                    teacher: formData.teacher || '',
                    groups: formData.groups || [],
                    room: formData.room || '',
                    subject: formData.subject || formData.course || '',
                    course: formData.subject || formData.course || '',
                    type: formData.type || 'class',
                    weekSeries: weekNumber.toString(), // Single week for this check
                    period_id: formData.period_id || formData.periodId || null
                };

                const tempScheduleEntry = new ScheduleEntry(tempScheduleEntryData);

                if (!tempScheduleEntry.isTimeValid) {
                    return null;
                }

                // Use ConflictManager to calculate conflicts
                const entryWithConflicts = conflictManager.calculateConflictForEntry(
                    tempScheduleEntry,
                    baseScheduleItems,
                    teachers,
                    groups,
                    subjects
                );

                // Get conflict info for this specific week
                const weekKey = `${weekNumber}-${currentYear}`;
                const weekConflictInfo = entryWithConflicts.conflictsByWeek?.get(weekKey);

                if (weekConflictInfo && weekConflictInfo.status !== 'green') {
                    return {
                        status: weekConflictInfo.status,
                        reasons: weekConflictInfo.reasons || []
                    };
                }

                return null;
            } catch (error) {
                console.error(`[ScheduleEntryWeekPicker] Error checking conflicts for week ${weekNumber}:`, error);
                return null;
            }
        };
    }, [isConflictDetectionEnabled, conflictDetectionData, currentYear]);

    // Update week conflicts when conflict detection data changes or selected weeks change
    useEffect(() => {
        if (!isConflictDetectionEnabled || !conflictDetectionData) {
            setWeekConflicts(new Map());
            return;
        }

        const newWeekConflicts = new Map();

        // Only check conflicts for selected weeks
        const checkWeekConflictsAsync = async () => {
            for (const week of selectedWeeks) {
                const conflictInfo = checkConflictsForWeek(week);
                if (conflictInfo) {
                    newWeekConflicts.set(week, conflictInfo);
                }
            }
            setWeekConflicts(newWeekConflicts);
        };

        // Debounce the conflict checking with a longer delay to reduce conflicts with EditScheduleItemModal
        const timeoutId = setTimeout(checkWeekConflictsAsync, 500);

        return () => clearTimeout(timeoutId);
    }, [isConflictDetectionEnabled, conflictDetectionData, checkConflictsForWeek, selectedWeeks]);

    // Parse initial value
    useEffect(() => {
        if (value) {
            const weeks = new Set();
            const parts = value.split(',').map(part => part.trim());

            parts.forEach(part => {
                if (part.includes('-')) {
                    const [start, end] = part.split('-').map(num => parseInt(num.trim()));
                    if (start <= end) {
                        for (let i = start; i <= end; i++) {
                            weeks.add(i);
                        }
                    } else {
                        // Handle year crossing (e.g., 52-4)
                        for (let i = start; i <= 53; i++) {
                            weeks.add(i);
                        }
                        for (let i = 1; i <= end; i++) {
                            weeks.add(i);
                        }
                    }
                } else {
                    const weekNum = parseInt(part);
                    if (!isNaN(weekNum) && weekNum >= 1 && weekNum <= 53) {
                        weeks.add(weekNum);
                    }
                }
            });
            setSelectedWeeks(weeks);
        } else {
            setSelectedWeeks(new Set());
        }
    }, [value]);

    // Convert selected weeks to string format
    const weeksToString = (weeks) => {
        if (weeks.size === 0) return '';

        const sortedWeeks = Array.from(weeks).sort((a, b) => a - b);
        const ranges = [];
        let start = sortedWeeks[0];
        let end = start;

        for (let i = 1; i < sortedWeeks.length; i++) {
            if (sortedWeeks[i] === end + 1) {
                end = sortedWeeks[i];
            } else {
                if (start === end) {
                    ranges.push(start.toString());
                } else {
                    ranges.push(`${start}-${end}`);
                }
                start = sortedWeeks[i];
                end = start;
            }
        }

        if (start === end) {
            ranges.push(start.toString());
        } else {
            ranges.push(`${start}-${end}`);
        }

        return ranges.join(',');
    };

    // Update parent component
    const updateParent = (newWeeks) => {
        const stringValue = weeksToString(newWeeks);
        onChange(stringValue);
    };

    // Toggle individual week
    const toggleWeek = (week) => {
        const newWeeks = new Set(selectedWeeks);
        if (newWeeks.has(week)) {
            newWeeks.delete(week);
        } else {
            newWeeks.add(week);
        }
        setSelectedWeeks(newWeeks);
        updateParent(newWeeks);
    };

    const monthAbbreviations = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    // Get date range for a specific week number
    const getWeekDateRange = (weekNumber) => {
        // Get the correct calendar year for this week based on academic year boundaries
        const weekCalendarYear = getCalendarYearForWeek(weekNumber);

        // Create a date for January 4th of the given year (always in week 1)
        const jan4 = new Date(weekCalendarYear, 0, 4);

        // Get the Monday of week 1 (ISO week date standard)
        const week1Monday = new Date(jan4);
        week1Monday.setDate(jan4.getDate() - ((jan4.getDay() + 6) % 7));

        // Calculate the Monday of the target week
        const targetMonday = new Date(week1Monday);
        targetMonday.setDate(week1Monday.getDate() + (weekNumber - 1) * 7);

        // Calculate the Sunday of the target week
        const targetSunday = new Date(targetMonday);
        targetSunday.setDate(targetMonday.getDate() + 6);

        // Format date range
        const startDay = targetMonday.getDate();
        const endDay = targetSunday.getDate();
        const startMonth = monthAbbreviations[targetMonday.getMonth()];
        const endMonth = monthAbbreviations[targetSunday.getMonth()];

        let dateRange;
        if (startMonth === endMonth) {
            // Same month: "15-22 Jun"
            dateRange = `${startDay}-${endDay} ${startMonth}`;
        } else {
            // Different months: "30Apr-5 May"
            dateRange = `${startDay}${startMonth}-${endDay} ${endMonth}`;
        }

        // Return both the date range and the calendar year
        return {
            dateRange,
            year: weekCalendarYear
        };
    };

    /** Calendar date for `scheduleDay` within ISO week `weekNumber`, or null if day unknown */
    const getScheduleDayDateLabel = (weekNumber, dayName) => {
        if (!dayName || typeof dayName !== 'string') return null;
        const weekCalendarYear = getCalendarYearForWeek(weekNumber);
        const jan4 = new Date(weekCalendarYear, 0, 4);
        const week1Monday = new Date(jan4);
        week1Monday.setDate(jan4.getDate() - ((jan4.getDay() + 6) % 7));
        const targetMonday = new Date(week1Monday);
        targetMonday.setDate(week1Monday.getDate() + (weekNumber - 1) * 7);

        const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const dayIndex = dayKeys.indexOf(dayName.toLowerCase().trim());
        if (dayIndex === -1) return null;

        const targetDate = new Date(targetMonday);
        if (dayIndex === 0) {
            targetDate.setDate(targetMonday.getDate() + 6);
        } else {
            targetDate.setDate(targetMonday.getDate() + (dayIndex - 1));
        }

        const weekdayShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][targetDate.getDay()];
        const mo = monthAbbreviations[targetDate.getMonth()];
        const d = targetDate.getDate();
        const y = targetDate.getFullYear();
        return `${weekdayShort}, ${d} ${mo} ${y}`;
    };

    // Shortcut functions
    const selectCurrentWeek = () => {
        const currentWeek = getCurrentWeek();
        const newWeeks = new Set([currentWeek]);
        setSelectedWeeks(newWeeks);
        updateParent(newWeeks);
    };

    const selectFullPeriod = () => {
        if (effectivePeriodBounds == null) {
            return;
        }
        const newWeeks = new Set(
            buildWeekNumbersFromStartEnd(effectivePeriodBounds.startWeek, effectivePeriodBounds.endWeek)
        );
        setSelectedWeeks(newWeeks);
        updateParent(newWeeks);
    };

    const clearSelection = () => {
        setSelectedWeeks(new Set());
        updateParent(new Set());
    };

    // Helper function to get exception for a specific week
    const getWeekException = (weekNumber) => {
        if (!exceptionsData?.exceptions || !Array.isArray(exceptionsData.exceptions)) return null;
        return exceptionsData.exceptions.find(ex => ex.week === weekNumber && ex.year === currentYear);
    };

    // Helper function to format exception changes for tooltip
    const formatExceptionChanges = (exception) => {
        if (!exception?.changes || !exceptionsData?.originalEntry) return [];

        const originalEntry = exceptionsData.originalEntry;
        const changes = [];

        // Helpers to normalize different data shapes
        const toHhMm = (timeStr) => {
            if (!timeStr || typeof timeStr !== 'string') return '';
            // Accept formats HH:MM or HH:MM:SS – return HH:MM
            const parts = timeStr.split(':');
            if (parts.length >= 2) return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
            return timeStr;
        };

        const formatRoom = (value) => {
            if (!value) return '';
            if (typeof value === 'string') return value;
            if (typeof value === 'object') {
                return value.name || value.room_code || String(value.id ?? '');
            }
            return String(value);
        };

        const formatTeacher = (value, fallbackFromArray) => {
            if (value) {
                if (typeof value === 'string') return value;
                if (typeof value === 'object') return value.name || String(value.id ?? '');
            }
            // Sometimes exceptions may carry teachers array
            if (Array.isArray(fallbackFromArray) && fallbackFromArray.length > 0) {
                const primary = fallbackFromArray.find(t => t?.role === 'primary') || fallbackFromArray[0];
                if (primary) return primary.name || String(primary.id ?? '');
            }
            return '';
        };

        // Compare time changes
        if (exception.changes.timeStart && exception.changes.timeEnd) {
            const originalTime = `${toHhMm(originalEntry.timeStart || '')} - ${toHhMm(originalEntry.timeEnd || '')}`;
            const exceptionTime = `${toHhMm(exception.changes.timeStart)} - ${toHhMm(exception.changes.timeEnd)}`;
            if (originalTime !== exceptionTime) {
                changes.push(`Time: ${originalTime} → ${exceptionTime}`);
            }
        }

        // Compare teacher changes
        {
            const oldTeacher = formatTeacher(originalEntry.teacher);
            const newTeacher = formatTeacher(exception.changes.teacher, exception.changes.teachers);
            if (newTeacher && newTeacher !== oldTeacher) {
                changes.push(`Teacher: ${oldTeacher || 'None'} → ${newTeacher}`);
            }
        }

        // Compare room changes
        {
            const oldRoom = formatRoom(originalEntry.room);
            const newRoom = formatRoom(exception.changes.room);
            if (newRoom && newRoom !== oldRoom) {
                changes.push(`Room: ${oldRoom || 'None'} → ${newRoom}`);
            }
        }

        return changes;
    };

    // Helper function to format conflict reasons for tooltip
    const formatConflictReasons = (conflictInfo) => {
        if (!conflictInfo?.reasons || conflictInfo.reasons.length === 0) return [];

        return conflictInfo.reasons.map(reason => ({
            text: reason.reason,
            severity: reason.severity
        }));
    };

    // Handle mouse enter for tooltip
    const handleMouseEnter = (event, week, weekException, conflictInfo) => {
        const weekDateInfo = getWeekDateRange(week);
        const scheduleDayDateLabel = getScheduleDayDateLabel(week, scheduleDay);

        setHoveredWeek({
            week,
            exception: weekException,
            conflict: conflictInfo,
            dateRange: weekDateInfo.dateRange,
            year: weekDateInfo.year,
            scheduleDayDateLabel,
            isCurrent: week === getCurrentWeek()
        });

        const rect = event.currentTarget.getBoundingClientRect();
        setTooltipPosition({
            x: rect.left + rect.width / 2,
            y: rect.top - 10
        });
    };

    // Handle mouse leave for tooltip
    const handleMouseLeave = () => {
        setHoveredWeek(null);
    };

    // Handle right-click on week button
    const handleWeekContextMenu = (event, week) => {
        // Only show context menu if onCreateException callback is provided
        if (!onCreateException) return;

        event.preventDefault();
        event.stopPropagation();

        setContextMenu({
            week,
            x: event.clientX,
            y: event.clientY
        });
    };

    // Handle context menu action
    const handleCreateException = () => {
        if (contextMenu && onCreateException) {
            onCreateException(contextMenu.week);
            setContextMenu(null);
        }
    };

    // Close context menu
    const handleCloseContextMenu = () => {
        setContextMenu(null);
    };

    // Close context menu when clicking outside or pressing Escape
    useEffect(() => {
        const handleClickOutside = () => {
            if (contextMenu) {
                handleCloseContextMenu();
            }
        };

        const handleEscape = (e) => {
            if (e.key === 'Escape' && contextMenu) {
                handleCloseContextMenu();
            }
        };

        if (contextMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [contextMenu]);

    // Render weeks grid
    const renderWeeksGrid = () => {
        const weeks = [];
        const weeksPerRow = 13; // Show 13 weeks per row for compact layout

        for (let week = 1; week <= 53; week++) {
            const isSelected = selectedWeeks.has(week);
            const isCurrent = week === getCurrentWeek();
            const weekException = getWeekException(week);
            const hasException = !!weekException;
            const conflictInfo = weekConflicts.get(week);
            const hasConflict = !!conflictInfo;
            const conflictSeverity = hasConflict ? conflictInfo.status : null;
            const isOutsideResolvedPeriod = !!(
                resolvedPeriod?.startWeek
                && resolvedPeriod?.endWeek
                && !isWeekInWeekRange(week, resolvedPeriod.startWeek, resolvedPeriod.endWeek)
            );

            weeks.push(
                <button
                    key={week}
                    type="button"
                    onClick={() => toggleWeek(week)}
                    onContextMenu={(e) => handleWeekContextMenu(e, week)}
                    onMouseEnter={(e) => handleMouseEnter(e, week, weekException, conflictInfo)}
                    onMouseLeave={handleMouseLeave}
                    className={`
                        relative w-8 h-8 text-xs font-medium rounded border transition-colors duration-200
                        ${isSelected
                            ? (isOutsideResolvedPeriod
                                ? 'bg-amber-500 text-white border-amber-500 hover:bg-amber-600'
                                : 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600')
                            : (isOutsideResolvedPeriod
                                ? 'bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50')
                        }
                        ${isCurrent ? 'ring-2 ring-orange-400' : ''}
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
                    `}
                >
                    {week}
                    {hasException && (
                        <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-yellow-400 text-yellow-900 rounded-full flex items-center justify-center text-[8px] font-bold leading-none shadow-sm border border-yellow-500">
                            !
                        </span>
                    )}
                    {hasConflict && (
                        <span className={`absolute -top-0.5 -left-0.5 w-3 h-3 rounded-full flex items-center justify-center text-[8px] font-bold leading-none shadow-sm ${conflictSeverity === 'red'
                            ? 'bg-red-500 text-white border border-red-600'
                            : conflictSeverity === 'orange'
                                ? 'bg-orange-500 text-white border border-orange-600'
                                : 'bg-gray-500 text-white border border-gray-600'
                            }`}>
                            ⚠
                        </span>
                    )}
                    {isSelected && isOutsideResolvedPeriod && (
                        <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-amber-200 text-amber-900 rounded-full flex items-center justify-center text-[8px] font-bold leading-none shadow-sm border border-amber-500">
                            !
                        </span>
                    )}
                </button>
            );
        }

        // Group weeks into rows
        const rows = [];
        for (let i = 0; i < weeks.length; i += weeksPerRow) {
            rows.push(
                <div key={i} className="flex gap-1">
                    {weeks.slice(i, i + weeksPerRow)}
                </div>
            );
        }

        return rows;
    };

    const outOfPeriodWeeks = getWeeksOutsideResolvedPeriod(value, resolvedPeriod);

    return (
        <div className={`space-y-3 ${className}`}>
            {/* Shortcuts */}
            <div className="flex flex-wrap gap-2">
                {showThisWeekShortcut ? (
                    <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={selectCurrentWeek}
                        className="text-xs"
                    >
                        This Week
                    </Button>
                ) : null}
                <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={selectFullPeriod}
                    className="text-xs"
                    disabled={effectivePeriodBounds == null}
                    title={effectivePeriodBounds == null ? 'Academic period bounds are not available yet' : undefined}
                >
                    Full Period
                </Button>
                <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={clearSelection}
                    className="text-xs"
                >
                    Clear
                </Button>
            </div>

            {/* Weeks grid */}
            <div className="bg-gray-50 p-3 rounded-lg">
                <div className="space-y-1">
                    {renderWeeksGrid()}
                </div>
                <div className="mt-2 text-xs text-gray-500">
                    <div className="flex items-center gap-4 flex-wrap">
                        <span className="flex items-center gap-1">
                            <div className="w-3 h-3 bg-blue-500 rounded border"></div>
                            Selected
                        </span>
                        <span className="flex items-center gap-1">
                            <div className="w-3 h-3 bg-white border border-gray-300 rounded ring-2 ring-orange-400"></div>
                            Current Week
                        </span>
                        <span className="flex items-center gap-1">
                            <div className="relative w-3 h-3 bg-white border border-gray-300 rounded">
                                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-yellow-400 text-yellow-900 rounded-full flex items-center justify-center text-[6px] font-bold leading-none shadow-sm border border-yellow-500">
                                    !
                                </span>
                            </div>
                            Exception
                        </span>
                        {isConflictDetectionEnabled && (
                            <span className="flex items-center gap-1">
                                <div className="relative w-3 h-3 bg-white border border-gray-300 rounded">
                                    <span className="absolute -top-0.5 -left-0.5 w-2 h-2 bg-red-500 text-white rounded-full flex items-center justify-center text-[6px] font-bold leading-none shadow-sm border border-red-600">
                                        ⚠
                                    </span>
                                </div>
                                Conflict
                            </span>
                        )}
                        {resolvedPeriod?.startWeek && resolvedPeriod?.endWeek && (
                            <span className="flex items-center gap-1">
                                <div className="w-3 h-3 bg-amber-50 border border-amber-300 rounded"></div>
                                Outside {resolvedPeriod.label}
                            </span>
                        )}
                        <span className="text-gray-700">
                            {selectedWeeks.size} week{selectedWeeks.size !== 1 ? 's' : ''} selected
                        </span>
                    </div>
                    {outOfPeriodWeeks.length > 0 && (
                        <div className="mt-1 text-amber-700">
                            Warning: week{outOfPeriodWeeks.length === 1 ? '' : 's'} {outOfPeriodWeeks.join(', ')} outside {resolvedPeriod.label}.
                        </div>
                    )}
                    {onCreateException && (
                        <div className="mt-1 text-gray-600 italic">
                            💡 Tip: Right-click on a week to create an exception for that specific week
                        </div>
                    )}
                </div>
            </div>

            {/* Text representation */}
            {selectedWeeks.size > 0 && (
                <div className="text-sm text-gray-600">
                    <strong>Selected:</strong> {weeksToString(selectedWeeks)}
                </div>
            )}

            {/* Custom Tooltip */}
            {hoveredWeek && (
                <div
                    className="fixed z-50 pointer-events-none"
                    style={{
                        left: `${tooltipPosition.x}px`,
                        top: `${tooltipPosition.y}px`,
                        transform: 'translateX(-50%) translateY(-100%)'
                    }}
                >
                    <div className="bg-gray-900 text-white text-xs rounded-md py-2 px-3 shadow-lg max-w-xs">
                        <div className="font-medium">
                            {hoveredWeek.scheduleDayDateLabel
                                ? <>Week {hoveredWeek.week} — {hoveredWeek.scheduleDayDateLabel}</>
                                : <>Week {hoveredWeek.week} - {hoveredWeek.dateRange} {hoveredWeek.year}</>}
                        </div>
                        {hoveredWeek.isCurrent && (
                            <div className="text-orange-300 text-[10px] mt-1">
                                (Current Week)
                            </div>
                        )}
                        {hoveredWeek.exception && (
                            <div className="mt-2 pt-2 border-t border-gray-700">
                                <div className="text-yellow-300 font-medium text-[10px] mb-1">
                                    EXCEPTION CHANGES:
                                </div>
                                {formatExceptionChanges(hoveredWeek.exception).map((change, index) => (
                                    <div key={index} className="text-[10px] text-gray-300">
                                        {change}
                                    </div>
                                ))}
                            </div>
                        )}
                        {hoveredWeek.conflict && (
                            <div className="mt-2 pt-2 border-t border-gray-700">
                                <div className={`font-medium text-[10px] mb-1 ${hoveredWeek.conflict.status === 'red'
                                    ? 'text-red-300'
                                    : hoveredWeek.conflict.status === 'orange'
                                        ? 'text-orange-300'
                                        : 'text-gray-300'
                                    }`}>
                                    {hoveredWeek.conflict.status === 'red' ? 'CONFLICTS:' : 'WARNINGS:'}
                                </div>
                                {formatConflictReasons(hoveredWeek.conflict).map((conflict, index) => (
                                    <div key={index} className={`text-[10px] ${conflict.severity === 'red'
                                        ? 'text-red-300'
                                        : conflict.severity === 'orange'
                                            ? 'text-orange-300'
                                            : 'text-gray-300'
                                        }`}>
                                        • {conflict.text}
                                    </div>
                                ))}
                            </div>
                        )}
                        {/* Arrow pointing down */}
                        <div
                            className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0"
                            style={{
                                borderLeft: '4px solid transparent',
                                borderRight: '4px solid transparent',
                                borderTop: '4px solid #1f2937'
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Context Menu */}
            {contextMenu && (
                <div
                    className="fixed z-50 bg-white rounded-md shadow-lg border border-gray-200 py-1 min-w-[200px]"
                    style={{
                        left: `${contextMenu.x}px`,
                        top: `${contextMenu.y}px`
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <button
                        type="button"
                        onClick={handleCreateException}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 flex items-center gap-2"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        <div>
                            <div className="font-medium">Make exception for week {contextMenu.week}</div>
                            <div className="text-xs text-gray-500 mt-0.5">
                                Customize this week independently
                            </div>
                        </div>
                    </button>
                </div>
            )}
        </div>
    );
};

export default ScheduleEntryWeekPicker; 